import { useCallback, useMemo, useState } from 'react';

import { IEmailAttachments, useEmailAttachments } from './useEmailAttachments';
import { IMailSignature, useMailSignatures } from './useMailSignatures';
import {
  composeEmailHtml,
  htmlToPlainText,
  isRichEmpty,
  pickSignature,
} from './richMailModel';

/** Что уходит в мутацию отправки: HTML (с подписью) и id вложений. */
export interface IRichEmailPayload {
  html: string;
  attachmentIds: string[];
}

interface IRichEmailDraftOptions {
  /** Ящик «От» — по нему выбирается подпись. */
  mailboxEmail?: string | null;
  /** Подпись к письму (по умолчанию — да). */
  withSignature?: boolean;
  initialHtml?: string;
}

/**
 * ТЗ 0097 — набираемое письмо: HTML редактора, подпись ящика (вкл/выкл),
 * вложения. `payload()` — то, что отправлять; `text` — видимый текст для
 * старых проверок (пусто, подстановки).
 *
 * Подпись НЕ живёт в HTML редактора — см. `composeEmailHtml`: она
 * показывается превью под полем и приклеивается при отправке.
 */
export const useRichEmailDraft = ({
  mailboxEmail = null,
  withSignature = true,
  initialHtml = '',
}: IRichEmailDraftOptions = {}) => {
  const [html, setHtml] = useState(initialHtml);
  const [signatureOn, setSignatureOn] = useState(true);
  const { signatures, loading: signaturesLoading } = useMailSignatures({
    skip: !withSignature,
  });
  const attachments: IEmailAttachments = useEmailAttachments();
  const signature: IMailSignature | null = useMemo(
    () => (withSignature ? pickSignature(signatures, mailboxEmail) : null),
    [withSignature, signatures, mailboxEmail],
  );
  const text = useMemo(() => htmlToPlainText(html), [html]);
  const empty = isRichEmpty(html);
  const { ids, uploading, clear } = attachments;

  const payload = useCallback(
    (): IRichEmailPayload => ({
      html: composeEmailHtml(
        html,
        withSignature && signatureOn && signature ? signature.html : null,
      ),
      attachmentIds: ids,
    }),
    [html, withSignature, signatureOn, signature, ids],
  );

  const reset = useCallback(() => {
    setHtml('');
    clear();
  }, [clear]);

  return {
    html,
    setHtml,
    text,
    empty,
    withSignature,
    signature,
    signaturesLoading,
    signatureOn,
    setSignatureOn,
    attachments,
    /** Можно отправлять: есть текст и файлы догружены (предел — сервер). */
    ready: !empty && !uploading,
    payload,
    reset,
  };
};

export type IRichEmailDraft = ReturnType<typeof useRichEmailDraft>;
